import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Crown, Heart, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import GameCanvas from "@/game/GameCanvas";
import GameOverDialog from "@/components/GameOverDialog";
import RaceDuelHUD from "@/components/RaceDuelHUD";
import { seedFromString, todayUtcKey } from "@/lib/seededRng";
import { getCurrentSkin } from "@/lib/skins";
import { bumpStreakOnDailyRun } from "@/lib/streak";
import { makeRecorder, fetchTopGhost } from "@/lib/replay";
import { fetchWeeklyChampion, isChampionName } from "@/lib/champion";
import { hasExtraLife, consumeExtraLife, grantExtraLife, didBeatGhost } from "@/lib/raceGhost";
import { checkAchievements } from "@/lib/achievements";
import { getIsSeason } from "@/lib/pro";
import { recordPlayDay } from "@/lib/seasonRewards";
import { syncPlayerState, saveActiveRun, clearActiveRun } from "@/lib/playerSync";
import { awardChipsForRun, spendChips, getChips, addChips as addChipsSafe } from "@/lib/chips";
import { weatherForRun } from "@/lib/weather";
import { bumpBuggySurvival, bumpWeatherWin, bumpLossStreak, resetLossStreak, isWagerCompNext, markWagerCompUsed, bumpRouletteResult, isRoulette5xPending, consumeRoulette5x, getRouletteStreak, pushRouletteHistory } from "@/lib/achievementsExtra";
import { pendingRoulette, consumeRoulette } from "@/lib/roulette";
import { depositJackpot } from "@/lib/jackpotVault";
import { getDuel, tickDuelAttempt, resolveDuel, consumeLifeToken, getLifeTokens, consumeDuelistBonus } from "@/lib/duel";
import { isSnipeBoostActive, SNIPE_BOOST_PCT } from "@/lib/snipeBoost";
import { detectNewlyUnlockedSkins } from "@/lib/skins";
import { getEmail } from "@/lib/pro";
import axios from "axios";

export default function PlayPage() {
  const { level } = useParams();
  const nav = useNavigate();
  const [endState, setEndState] = useState(null);
  const [runKey, setRunKey] = useState(0);
  const [ghost, setGhost] = useState(null);
  const [champion, setChampion] = useState(null);
  const [liveScore, setLiveScore] = useState(0);
  const [duelHud, setDuelHud] = useState(null); // { handle, ghostScore }

  const isDaily = level === "daily";
  const validLevel =
    level === "underground" ? "underground" :
    level === "rooftop" ? "rooftop" :
    level === "highway" ? "highway" :
    level === "snowstorm" ? "snowstorm" :
    level === "tunnels" ? "tunnels" :
    level === "beach" ? "beach" :
    level === "neon_rain" ? "neon_rain" : "outdoor";
  const daySeed = isDaily ? seedFromString(todayUtcKey()) : null;
  const submitLevel = isDaily ? "daily" : validLevel;
  const skin = getCurrentSkin();

  // Season Pass perks
  const seasonActive = getIsSeason();
  const seasonExtraLife = seasonActive;                    // +1 starting life for every run
  const seasonScoreMultiplier = seasonActive ? 2 : 1;      // 2× XP/score
  // Race-the-ghost bonus is only for daily runs
  const raceBonusExtraLife = isDaily && hasExtraLife();
  // Duel life tokens: consume one per run if available (any level)
  const [duelLifeConsumed] = useState(() => (getLifeTokens() > 0 ? consumeLifeToken() : false));
  const extraLifeForThisRun = seasonExtraLife || raceBonusExtraLife || duelLifeConsumed;
  const [showedExtraLifeBanner, setShowedExtraLifeBanner] = useState(false);
  // Weather is a Pro/Season perk — read once per run
  const runWeather = seasonActive ? weatherForRun() : null;
  // Roulette ticket — free daily random-weather challenge (any tier)
  const roulette = pendingRoulette();
  const activeWeather = roulette ? { type: roulette.type, intensity: roulette.intensity } : runWeather;
  // Wager state (chips staked for 2× payout on win, only when weather is on)
  const [wager, setWager] = useState(0); // 0 = no wager active
  const [compNext, setCompNext] = useState(() => isWagerCompNext());

  const recorderRef = useRef(makeRecorder());

  // Save an active-run snapshot on mount so cross-device resume knows where the player was.
  useEffect(() => {
    saveActiveRun({ level: submitLevel });
    return () => {
      // On page leave (nav away without finish), also clear so we don't nag on menu.
      // Finish path clears explicitly, so an early-exit is a hard clear too.
      clearActiveRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitLevel, runKey]);

  useEffect(() => {
    if (!isDaily) return;
    fetchTopGhost().then((g) => {
      if (g && g.replay && g.replay.length > 0) setGhost(g);
    });
    fetchWeeklyChampion().then((c) => {
      if (c && c.player_name) setChampion(c);
    });
    // Consume the extra-life flag once we've read it for this run
    if (hasExtraLife() && !showedExtraLifeBanner) {
      consumeExtraLife();
      setShowedExtraLifeBanner(true);
      toast.success("+1 LIFE ACTIVE · reward for beating yesterday's champion");
    }
    // Duel: tick attempt for this daily run
    const duel = getDuel();
    if (duel) {
      tickDuelAttempt();
      setDuelHud({ handle: duel.handle, ghostScore: duel.ghostScore });
      toast.info(`DUEL · Beat ${duel.handle} (${duel.ghostScore}) on first try for +2 LIVES`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDaily, showedExtraLifeBanner]);
  const finish = (r, won) => {
    const samples = recorderRef.current.getSamples().slice();
    let beatGhost = false;
    // Record a play-day for Season Track (no-op if not season active)
    recordPlayDay();
    // Award chips (buggy survival + weather wager stack + roulette 3× on win, +5× streak)
    // JACKPOT (5×): chips go into a 24h Jackpot Vault (+50% bonus if untouched)
    // rather than being paid directly. The other multipliers still pay out.
    // DUELIST BONUS: after 3+ clean duel wins, one daily run pays 3× on top.
    const wagerMult = wager > 0 ? 2 : 1;
    const duelistBonusOn = isDaily ? consumeDuelistBonus() : false;
    const duelistMult = duelistBonusOn ? 3 : 1;
    let rouletteMult = 1;
    let usedRoulette5x = false;
    let jackpotDeposit = 0;
    if (roulette) {
      if (isRoulette5xPending()) {
        rouletteMult = 5;
        usedRoulette5x = true;
      } else {
        rouletteMult = 3;
      }
    }
    // If jackpot: award chips at 1× rouletteMult, compute the 5× delta, send to vault
    let totalChips;
    if (usedRoulette5x && won) {
      // Pay out non-jackpot portion (baseline win reward)
      const baseline = awardChipsForRun({ score: r.score, won, beatBoss: !!r.beatBoss, wagerMult: wagerMult * duelistMult, rouletteMult: 1 });
      // Compute what a 5× win would have paid, and vault the delta
      const base = Math.floor(r.score / 10);
      const bonus = 25;
      const bossMult = (r.beatBoss ? 3 : 1);
      const fullFive = Math.floor((base + bonus) * bossMult * wagerMult * duelistMult * 5);
      const oneX = Math.floor((base + bonus) * bossMult * wagerMult * duelistMult * 1);
      jackpotDeposit = Math.max(0, fullFive - oneX);
      if (jackpotDeposit > 0) depositJackpot(jackpotDeposit);
      totalChips = baseline;
    } else {
      totalChips = awardChipsForRun({ score: r.score, won, beatBoss: !!r.beatBoss, wagerMult: wagerMult * duelistMult, rouletteMult });
    }
    // Snipe Streak Boost: +10% on top of everything while active
    if (isSnipeBoostActive() && totalChips > 0) {
      const bonus = Math.floor(totalChips * SNIPE_BOOST_PCT);
      if (bonus > 0) {
        addChipsSafe(bonus);
        toast.success(`SNIPE BOOST · +${bonus} bonus chips (+${Math.round(SNIPE_BOOST_PCT * 100)}%)`);
        totalChips += bonus;
      }
    }
    if (duelistBonusOn && won) toast.success(`DUELIST 3× · streak bonus chips applied`);
    let newRouletteStreak = getRouletteStreak();
    if (roulette) {
      consumeRoulette();
      if (usedRoulette5x) consumeRoulette5x();
      newRouletteStreak = bumpRouletteResult(won);
      pushRouletteHistory({
        won,
        weather: roulette.type,
        chips: won ? totalChips : 0,
        jackpot: usedRoulette5x,
        streak: newRouletteStreak,
        score: r.score,
      });
      if (won) toast.success(usedRoulette5x ? `JACKPOT VAULTED · +${jackpotDeposit} chips locked 24h (+50% bonus)` : `ROULETTE WON · 3× chip payout`);
    }
    // Wager loss/win tracking — comp streak & refund handling
    if (wager > 0) {
      if (won) {
        toast.success(`WAGER WON · 2× payout applied`);
        resetLossStreak();
      } else {
        if (compNext) {
          // Comp: refund the wager on loss
          addChipsSafe(wager);
          markWagerCompUsed();
          resetLossStreak();
          toast.success(`WAGER REFUNDED · your ${wager} chips are safe`);
        } else {
          const n = bumpLossStreak();
          toast.error(`LOST ${wager} CHIPS · wager forfeited${n >= 2 ? " · NEXT ONE IS ON US" : ""}`);
        }
      }
    }
    if (r.beatBoss && won) {
      const n = bumpBuggySurvival();
      toast.success(`BEACH BUGGY SURVIVED · 3× chips (${n}/3)`);
      // Log to weekly buggy leaderboard
      const em = getEmail();
      if (em) {
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/buggy/log`, {
          email: em,
          weather: runWeather?.type || "off",
        }).catch(() => {});
      }
    }
    // Weather wins → Storm Rider skin track
    if (won && runWeather && runWeather.type !== "off") {
      bumpWeatherWin(runWeather.type);
    }
    // Weather wager ledger → backend for the wager leaderboard
    const email = getEmail();
    if (wager > 0 && email) {
      const net = won ? totalChips - wager : -wager;
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wager/log`, {
        email,
        wagered: wager,
        won,
        weather: runWeather?.type || "off",
        net_chips: net,
      }).catch(() => {});
    }
    // Clan contribution → backend for the clan leaderboard (any positive chip run)
    if (email && totalChips > 0) {
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/clan/log-contribution`, {
        email,
        chips: totalChips,
      }).catch(() => {});
    }
    // Trigger skin-unlock toast for rare drops
    const newlySkin = detectNewlyUnlockedSkins();
    for (const s of newlySkin) toast.success(`SKIN UNLOCKED · ${s.name}`);
    if (isDaily) {
      const bump = bumpStreakOnDailyRun();
      if (bump && bump.freezeUsed) toast.success("STREAK FROZEN · you used a Streak Freeze");
      // Race the Ghost: if player beat today's ghost (score AND survival), grant reward.
      if (ghost && didBeatGhost(r.score, r.time, ghost)) {
        beatGhost = true;
        grantExtraLife();
        toast.success(`YOU BEAT ${ghost.player_name}! +1 LIFE NEXT RUN`);
      }
      // Champion Duel resolution: was this a queued CHALLENGE THEM run?
      if (getDuel()) {
        const outcome = resolveDuel({ playerScore: r.score, won });
        if (outcome.cleanFirstWin) {
          toast.success(`DUEL WON · ${outcome.handle} bested · +${outcome.awarded} LIVES · STREAK ${outcome.streak}`);
          if (outcome.duelistUnlocked) {
            toast.success(`◆ DUELIST TITLE UNLOCKED · next daily pays 3× chips`);
          }
        } else if (won) {
          toast.info(`DUEL · not a clean first win (needed >${outcome.ghostScore})`);
        } else {
          toast.info(`DUEL · lost to ${outcome.handle} · try again tomorrow`);
        }
      }
    }
    // Check achievements
    const newly = checkAchievements({ score: r.score, time: r.time, level: submitLevel, won, beatGhost });
    for (const a of newly) {
      toast.success(`ACHIEVEMENT · ${a.name}`);
    }
    setEndState({
      ...r,
      won,
      level: submitLevel,
      replay: isDaily ? samples : undefined,
      roulette: roulette ? { type: roulette.type, jackpot: usedRoulette5x, streak: newRouletteStreak } : null,
      weather: activeWeather ? activeWeather.type : null,
    });
    // Clear the resume snapshot since the run finished
    clearActiveRun();
    // Persist to Mongo (no-op if no email)
    syncPlayerState();
  };

  const retry = () => {
    recorderRef.current.clear();
    setEndState(null);
    setRunKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen w-full grid-bg py-6" data-testid="play-page">
      <GameCanvas
        key={runKey + submitLevel}
        level={validLevel}
        seed={daySeed}
        skinColor={skin.color}
        skinGlow={skin.glow}
        ghostReplay={isDaily && ghost ? ghost.replay : null}
        onSample={isDaily ? (t, x, y, score) => {
          recorderRef.current.onSample(t, x, y);
          if (duelHud && typeof score === "number") setLiveScore(score);
        } : null}
        extraLife={extraLifeForThisRun}
        scoreMultiplier={seasonScoreMultiplier}
        weather={activeWeather}
        onExit={() => nav("/")}
        onGameOver={(r) => finish(r, false)}
        onWin={(r) => finish(r, true)}
      />
      {isDaily && duelHud && !endState && (
        <RaceDuelHUD
          rivalHandle={duelHud.handle}
          rivalScore={duelHud.ghostScore}
          currentScore={liveScore}
        />
      )}
      {isDaily && ghost && ghost.player_name && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-white/30 bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-white/80"
          data-testid="ghost-indicator"
        >
          <span className="w-2 h-2 rounded-full bg-white/70" style={{ boxShadow: "0 0 8px rgba(255,255,255,0.7)" }} />
          GHOST · {ghost.player_name}
          {isChampionName(champion, ghost.player_name) && (
            <Crown size={12} className="text-[#FFD400]" style={{ filter: "drop-shadow(0 0 6px rgba(255,212,0,0.7))" }} />
          )}
          · {String(ghost.score).padStart(6, "0")} · {(ghost.survival_time || 0).toFixed(1)}s
          <span className="ml-2 pl-2 border-l border-white/20 flex items-center gap-1 text-[#FFD400]">
            <Zap size={11} /> BEAT FOR +1 LIFE
          </span>
        </div>
      )}
      {extraLifeForThisRun && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-[#FF0055] bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#FF0055]"
          data-testid="extra-life-indicator"
          style={{ boxShadow: "0 0 16px rgba(255,0,85,0.35)" }}
        >
          <Heart size={11} className="fill-[#FF0055]" /> +1 LIFE ACTIVE
        </div>
      )}
      {seasonActive && (
        <div
          className="fixed top-28 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-[#F0ABFC] bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#F0ABFC]"
          data-testid="season-perk-indicator"
          style={{ boxShadow: "0 0 16px rgba(240,171,252,0.35)" }}
        >
          <Sparkles size={11} /> SEASON PASS · 2× SCORE
        </div>
      )}
      {seasonActive && runWeather && wager === 0 && !endState && (
        <div
          className="fixed top-40 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2 border border-[#FFD400] bg-black/70 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#FFD400]"
          data-testid="weather-wager-banner"
          style={{ boxShadow: "0 0 20px rgba(255,212,0,0.35)" }}
        >
          <span>{compNext ? "FREE WAGER · REFUNDED ON LOSS · 2× ON WIN" : "WEATHER WAGER · 2× ON WIN"}</span>
          {[25, 50, 100].map((amt) => (
            <button
              key={amt}
              data-testid={`wager-${amt}-button`}
              onClick={() => {
                const r = spendChips(amt);
                if (!r.ok) {
                  toast.error(`NEED ${amt} CHIPS · you have ${r.have}`);
                  return;
                }
                setWager(amt);
                if (compNext) toast.success(`STAKED ${amt} CHIPS · comp active`);
                else toast.success(`STAKED ${amt} CHIPS · win to double`);
              }}
              className="px-2 py-1 border border-[#FFD400]/60 hover:bg-[#FFD400] hover:text-black transition-colors"
            >
              {amt}
            </button>
          ))}
          <span className="opacity-60 border-l border-white/20 pl-3">{getChips()} available</span>
        </div>
      )}
      {seasonActive && runWeather && wager > 0 && (
        <div
          className="fixed top-40 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-[#FFD400] bg-black/70 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#FFD400]"
          data-testid="weather-wager-active"
          style={{ boxShadow: "0 0 16px rgba(255,212,0,0.55)" }}
        >
          <Sparkles size={11} /> {compNext ? `COMP WAGER · ${wager} STAKED · REFUND ON LOSS` : `WAGER LIVE · ${wager} STAKED · 2× ON WIN`}
        </div>
      )}
      <GameOverDialog
        open={!!endState}
        result={endState}
        onRetry={retry}
        onMenu={() => {
          setEndState(null);
          nav("/");
        }}
        onLeaderboard={() => {
          setEndState(null);
          nav("/leaderboard");
        }}
      />
    </div>
  );
}
